"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { BrandMark } from "@/components/brand-mark";
import { useAuth } from "@/contexts/auth-context";
import { updateWorkspaceSettings } from "@/lib/api/usage";
import { mutateWorkspaceSettings } from "@/lib/queries";
import { firmWebsitePrefill, looksLikeFirmWebsite } from "@/lib/firm-website";

/**
 * Post-sign-in blocking screen for the domain-first onboarding gate
 * (ENG-3641). One question — firm website — plus a secondary "We're a
 * new firm" path. Submitting records `onboarding_state` on workspace
 * settings and routes to `/new`. No agentic trigger here (ENG-3643).
 *
 * Rendered by AuthGuard when the `onboarding_gate` feature is granted
 * and the active workspace has not yet answered.
 */
export function OnboardingGateScreen() {
  const { user, me, markOnboardingComplete } = useAuth();
  const router = useRouter();
  const email = me?.email ?? user?.email ?? null;
  const [website, setWebsite] = useState(() => firmWebsitePrefill(email));
  const [busy, setBusy] = useState<"website" | "new_firm" | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(path: "website" | "new_firm") {
    if (busy) return;
    setError(null);
    if (path === "website" && !looksLikeFirmWebsite(website)) {
      setError("Enter a valid website (e.g. yourfirm.com).");
      return;
    }
    setBusy(path);
    try {
      const next = await updateWorkspaceSettings({
        onboarding_state: {
          path,
          website: path === "website" ? website.trim() : null,
        },
      });
      // Keep SWR/settings caches in sync so a later settings read doesn't
      // briefly show the unanswered state and re-prompt the gate.
      void mutateWorkspaceSettings(next);
      markOnboardingComplete({
        path: next.onboarding_state?.path ?? path,
        website: next.onboarding_state?.website ?? (path === "website" ? website.trim() : null),
        completed_at: next.onboarding_state?.completed_at ?? Date.now() / 1000,
      });
      router.replace("/new");
    } catch {
      setError("Couldn't save that — please try again.");
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md w-full space-y-6 animate-fade-in">
        <BrandMark
          alt="Deliverables AI"
          className="h-8 w-8 mx-auto"
        />
        <div className="text-center space-y-2">
          <h1 className="text-xl font-semibold tracking-tight">
            What&apos;s your firm&apos;s website?
          </h1>
          <p className="text-sm text-subtle leading-relaxed">
            We&apos;ll use it to set up your workspace. Nothing else to fill
            in right now.
          </p>
        </div>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit("website");
          }}
        >
          <label className="block">
            <span className="sr-only">Firm website</span>
            <input
              type="text"
              inputMode="url"
              autoComplete="url"
              autoFocus
              placeholder="yourfirm.com"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              disabled={busy !== null}
              className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 disabled:opacity-50"
            />
          </label>
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy !== null}
            className="w-full px-4 py-2.5 rounded-lg bg-accent text-accent-ink text-sm font-medium hover:opacity-95 disabled:opacity-50 transition-colors"
          >
            {busy === "website" ? "…" : "Continue"}
          </button>
        </form>

        <div className="text-center">
          <button
            type="button"
            onClick={() => void submit("new_firm")}
            disabled={busy !== null}
            className="text-sm text-subtle hover:text-foreground disabled:opacity-50 transition-colors"
          >
            {busy === "new_firm" ? "…" : "We're a new firm"}
          </button>
        </div>
      </div>
    </div>
  );
}
